import type { ApiScopesGetResponseBody } from '@ipfabric/utils';
import type {
  GlobalStateCommandFactory,
  LicenseInfoApiResponse,
} from 'components/GlobalState/GlobalStateModel';
import { reconnectSocket } from 'components/Socket/ipfSocket';
import { LOAD_USER_SETTINGS } from 'pages/Auth/constants';
import { changeSnapshot } from 'pages/SnapshotManagement/actions';
import { PAGE_URL as SNAPSHOTS_PAGE_URL } from 'pages/SnapshotManagement/constants';
import { fetchSnapshotsSaga } from 'pages/SnapshotManagement/sagas';
import { type Saga, runSaga } from 'redux-saga';
import { niceBytes } from './helpers/niceBytes';

export const authenticate: GlobalStateCommandFactory =
  (cmdContext) => async () => {
    const { pathname } = cmdContext.getState().location;
    if (pathname.startsWith(SNAPSHOTS_PAGE_URL)) {
      const snapshotId = pathname.split('/')[2];
      if (
        snapshotId &&
        !snapshotId.startsWith('tasks') &&
        !snapshotId.startsWith('errors')
      ) {
        await cmdContext.reduxDispatch(changeSnapshot(snapshotId));
      }
    }

    try {
      cmdContext.updateState((draft) => {
        draft.loginStatus = 'LOGGING_IN';
      });
      const [version, { diskSpace }, user, apiScopes, userSettings, license] =
        await Promise.all([
          cmdContext.api.request({ url: '/os/version' }),
          cmdContext.api.request({ url: '/os/disk-space' }),
          cmdContext.api.request({ url: '/users/me' }),
          cmdContext.api.request<ApiScopesGetResponseBody>({
            url: 'users/me/scopes/api',
          }),
          cmdContext.api.request({ url: '/users/me/settings' }),
          cmdContext.api
            .request<LicenseInfoApiResponse>({
              url: '/licenses/info',
            })
            .catch(() => ({
              devices: null,
              ccu: undefined,
              additionalFeatures: undefined,
            })),

          runSaga(
            {
              dispatch: cmdContext.reduxDispatch,
              getState: cmdContext.reduxStore.getState,
            },
            fetchSnapshotsSaga as Saga,
            {},
          ).toPromise(),
        ]);

      cmdContext.updateState((draft) => {
        draft.version = version.version;
        draft.user.username = user.username;
        draft.user.timezone = user.timezone;
        draft.user.ldapId = user.ldapId;
        draft.user.isLocal = user.isLocal;
        draft.user.pinnedPages = userSettings.pinnedPages ?? [];
        draft.loginStatus = 'AUTHENTICATED';
        draft.auth.apiScopes = apiScopes.data;
        draft.auth.admin = user.roleIds.includes('admin');
        draft.auth.roleIds = user.roleIds;
        draft.license.devices = license.devices ?? null;
        draft.license.ccu = license.ccu;
        draft.license.additionalFeatures = license.additionalFeatures;
      });
      // Logout and login again to authenticate user if socket was connected without being authenticated
      // or if user was not connected to socket at all.
      reconnectSocket();

      if (
        diskSpace !== '' &&
        // biome-ignore lint/suspicious/noGlobalIsNan: obviously intentional use of coercion
        !isNaN(diskSpace) &&
        Number(diskSpace) < 5242880
      ) {
        cmdContext.showNotification({
          type: 'warning',
          title: cmdContext.getState().msg.auth.request.outOfDiskSpaceTitle,
          message: cmdContext
            .getState()
            .msg.auth.request.outOfDiskSpaceMessage.replace(
              '{space}',
              niceBytes(diskSpace),
            ),
        });
      }

      type LoginHook = (attrs: { id: string; name: string }) => void;
      for (const loginHook of ((window as any).ipfLoginHooks ??
        []) as LoginHook[]) {
        loginHook({
          id: user.id,
          name: user.username,
        });
      }

      cmdContext.reduxDispatch({
        type: `${LOAD_USER_SETTINGS}_SUCCESS`,
        payload: userSettings,
      });
    } finally {
      cmdContext.updateState((draft) => {
        if (draft.loginStatus === 'LOGGING_IN') {
          draft.loginStatus = 'LOGGED_OUT';
        }
      });
    }
  };
